// ui/src/utils/highlight.js
import { H } from 'highlight.run';
import { config } from '../config';

const HIGHLIGHT_PROJECT_ID = process.env.REACT_APP_HIGHLIGHT_PROJECT_ID;
const isDevelopment = process.env.NODE_ENV === 'development';
let initialized = false;

// Initialize Highlight with comprehensive configuration
export const initHighlight = () => {
  // Skip initialization in test environment
  if (process.env.NODE_ENV === 'test' || initialized) return;

  // If project id is missing, skip to avoid runtime warnings
  if (!HIGHLIGHT_PROJECT_ID || HIGHLIGHT_PROJECT_ID === 'project-id') {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.warn('Highlight not initialized: missing REACT_APP_HIGHLIGHT_PROJECT_ID');
    }
    return;
  }

  // Build safe tracing origins (strings only)
  const tracingOrigins = ['localhost', '127.0.0.1'];
  const apiUrl = config?.apiUrl;
  if (typeof apiUrl === 'string' && apiUrl.length > 0) {
    try {
      const host = new URL(apiUrl).host || apiUrl.replace(/^https?:\/\//, '');
      if (typeof host === 'string' && host) tracingOrigins.push(host);
    } catch {
      const host = apiUrl.replace(/^https?:\/\//, '');
      if (host) tracingOrigins.push(host);
    }
  }

  H.init(HIGHLIGHT_PROJECT_ID, {
    serviceName: 'assistant-ui',
    environment: process.env.NODE_ENV || 'development',

    // Enable network recording to capture API calls
    networkRecording: {
      enabled: true,
      recordHeadersAndBody: true,
      // Use strings (not regex) to avoid library .toLowerCase errors
      urlBlocklist: ['/auth/', '/login', '/token'],
    },

    // Redaction & recording controls
    networkHeadersToRedact: ['authorization', 'cookie', 'x-api-key', 'proxy-authorization', 'set-cookie'],
    networkBodyKeysToRedact: ['password', 'token', 'secret', 'key', 'client_secret', 'authorization'],
    networkHeaderKeysToRecord: [],
    networkBodyKeysToRecord: [],

    // Configure tracing origins for backend correlation (strings only)
    tracingOrigins,

    // Console recording - only in development
    consoleRecording: { enabled: isDevelopment },
    // Ensure console errors and unhandled rejections are reported even if consoleRecording is off
    reportConsoleErrors: true,
    enablePromisePatch: true,

    // Privacy settings
    privacySettings: {
      inputBlocklist: ['password', 'token', 'secret', 'key'],
      textBlocklist: ['password', 'token', 'secret', 'key'],
    },

    // Only enable verbose logging in development
    debug: isDevelopment,
  });

  // Ensure recording is started
  try {
    H.initialize({ silent: true });
  } catch {}

  // Attach global error listeners to ensure error capture
  try {
    window.addEventListener('error', (event) => {
      try {
        const err = event?.error || new Error(event?.message || 'Unhandled window error');
        H.consumeError(err);
      } catch {}
    });
    window.addEventListener('unhandledrejection', (event) => {
      try {
        const reason = event?.reason instanceof Error ? event.reason : new Error(String(event?.reason || 'Unhandled promise rejection'));
        H.consumeError(reason);
      } catch {}
    });
  } catch {}

  initialized = true;
};

// Identify user after authentication
export const identifyUser = (user, metadata = {}) => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  H.identify(user.email || user.sub, {
    email: user.email,
    name: user.name,
    tenant_id: metadata.tenant_id,
    ...metadata,
  });
};

// Track custom events
export const trackEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  H.track(eventName, properties);
};

// Add custom attributes to session
export const addSessionAttributes = (attributes) => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  Object.entries(attributes).forEach(([key, value]) => {
    try {
      // addSessionFeedback records arbitrary key/value metadata on the session
      H.addSessionFeedback(key, value);
    } catch {
      // No-op if API changes or unavailable
    }
  });
};

export default H;
